import { multipleFileDelete } from "apis/documents";
import { openDownloadUrl } from "util/openDownloadUrl";
import { Button } from "components/DesignSystem/Button/Button";
import { Cross } from "components/icons/Cross";
import { DividerLine } from "components/icons/DividerLine";
import { SelectedFloatingControl } from "components/SelectedFloatingControl/SelectedFloatingControl";
import {
  FILE_FOLDER_DELETED,
  FILE_FOLDER_DELETE_FAILED,
  FILE_FOLDER_DOWNLOADED,
  FILE_FOLDER_DOWNLOAD_FAILED,
} from "constants/analyticsEvents";
import { FOLDER_TYPE } from "constants/documents";
import { useAnalytics } from "hooks/useAnalytics";
import { useCopyToClipboard } from "hooks/useCopyToClipboard";
import { useCurrentGroupContext } from "hooks/useCurrentGroupContext";
import { useModal } from "hooks/useModal";
import { useQuery } from "hooks/useQuery";
import { useToast } from "hooks/useToast";
import { useContext, useMemo, useState } from "react";
import {
  useAddToRecycleBinMutation,
  useExportFolderMutation,
  useLazyGetSearchContentQuery,
  useRenameFileFolderMutation,
} from "store/apis/documents";
import { fileAndFolder } from "types/Models/documents";
import { BackendError } from "types/utils/error";
import { pluralize } from "utils/pluralize";
import { ConfirmMoveToRecycleBin } from "./ConfirmMoveToRecycleBin";
import { DeleteConfirmationModal } from "./DeleteConfirmationModal";
import { DocumentBulkAction } from "./DocumentBulkAction";
import { DocumentContext } from "./documentContext";
import { MoveDocModal } from "./documentsModal/MoveDocModal";
import { RenameFileFolderModal } from "./documentsModal/renameFileFolderModal";

export const DocumentBulkActionFloat = () => {
  const { trackEvent } = useAnalytics();
  const {
    selectedFiles,
    setSelectedFiles,
    selectedRecentDocs,
    setCurrentPageNo,
    setEntityFileAndFolder,
    handleFolderClick,
    entityFileAndFolder,
  } = useContext(DocumentContext);
  const query = useQuery();
  const {
    alertToast,
    successToast,
    loading: loadingToast,
    dismiss: dismissToast,
  } = useToast();
  const searchTerm = query.get("search");
  const group = useCurrentGroupContext();
  const [isDeleting, setDeleting] = useState(false);
  const groupId = group?.uuid;

  const selectedFileData = useMemo(
    () => [...selectedFiles, ...selectedRecentDocs],
    [selectedFiles, selectedRecentDocs]
  );
  const selectedItemLength = selectedFileData.length;

  const selectedFileIds = useMemo(
    () => selectedFileData.map(({ uuid }: { uuid: string }) => uuid),
    [selectedFileData]
  );

  const deleteModal = useModal();
  const renameModal = useModal();
  const moveFileModal = useModal();
  const moveToRecycleBin = useModal();

  const [getSearchContent] = useLazyGetSearchContentQuery();
  const [exportFolder] = useExportFolderMutation();
  const [renameFileAndFolder] = useRenameFileFolderMutation();
  const { copyToClipboard } = useCopyToClipboard();
  const [addContentToBin, { isLoading: isMovingToBin }] =
    useAddToRecycleBinMutation();

  const linkText =
    selectedFileData[0]?.type === FOLDER_TYPE
      ? `${window.location.origin}/docs/${groupId}/${selectedFileData[0]?.uuid}`
      : `${window.location.origin}/file-preview/${groupId}/${selectedFileData[0]?.uuid}`;

  const containsPrivateDocuments = selectedFileData.some(
    ({ is_private }: { is_private?: boolean }) => is_private
  );

  const multipleDownloads = async (
    e?: React.MouseEvent<HTMLButtonElement>,
    exportToMail?: boolean
  ) => {
    e?.stopPropagation();
    const loadingtoastId = loadingToast({
      title: "Preparing Export",
      message: "This may take upto one minute",
    });
    try {
      const response = await exportFolder({
        groupId,
        fileId: selectedFileIds as unknown as string,
        export_to_mail: exportToMail,
      }).unwrap();

      if (!exportToMail) {
        const { download_url } = response;
        successToast({ title: "Export Completed" });
        openDownloadUrl(download_url);
        trackEvent(FILE_FOLDER_DOWNLOADED, {
          file_or_folder_ids: selectedFileIds,
        });
      } else {
        successToast({
          title: "Export in progress",
          message: "Documents will be sent to your email once done",
        });
      }
    } catch (error) {
      alertToast({ message: (error as BackendError)?.data?.error?.message });
      trackEvent(FILE_FOLDER_DOWNLOAD_FAILED, {
        file_or_folder_ids: selectedFileIds,
        error: (error as BackendError)?.data?.error?.message!,
      });
    } finally {
      dismissToast(loadingtoastId);
    }
  };

  const multipleDelete = async () => {
    try {
      setDeleting(true);
      const response = await multipleFileDelete({
        groupId,
        fileIds: selectedFileIds,
      });
      trackEvent(FILE_FOLDER_DELETED, { file_or_folder_ids: selectedFileIds });

      if (searchTerm) {
        const {
          files = [],
          folders = [],
          current_page,
        } = await getSearchContent({
          searchTerm,
          groupId,
          pageNum: 1,
        }).unwrap();
        setCurrentPageNo(current_page);

        const updatedMapData = [...folders, ...files].map(
          (ele: Record<string, unknown>) => ({
            ...ele,
            isChecked: false,
          })
        );
        setEntityFileAndFolder(updatedMapData);
      } else {
        const updatedMapData = [
          ...response.data.data.subfolders,
          ...response.data.data.files,
        ].map((ele: Record<string, unknown>) => ({
          ...ele,
          isChecked: false,
        }));
        setEntityFileAndFolder(updatedMapData);
      }

      setSelectedFiles([]);
      deleteModal.close();
      successToast({
        title: "Deleted successfully",
        message: `The ${pluralize(
          selectedFileIds.length,
          "file",
          "files",
          false
        )} has been permanently deleted`,
      });
    } catch (error) {
      trackEvent(FILE_FOLDER_DELETE_FAILED, {
        file_or_folder_ids: selectedFileIds,
        error: (error as BackendError)?.data?.error?.message!,
      });
      alertToast({ message: (error as BackendError)?.data?.error?.message });
    } finally {
      setDeleting(false);
    }
  };

  const handleMoveToRecycleBin = async () => {
    try {
      await addContentToBin({
        groupId,
        files: selectedFileIds,
      }).unwrap();

      setEntityFileAndFolder((prev: fileAndFolder[]) =>
        prev.filter((file) => !selectedFileIds.includes(file.uuid))
      );

      setSelectedFiles([]);
      moveToRecycleBin.close();
      successToast({
        title: "Moved to recycle bin",
        message: `The ${pluralize(
          selectedFileIds.length,
          "file",
          "files",
          false
        )} has been moved to recycle bin`,
      });
    } catch (error) {
      alertToast({ message: (error as BackendError)?.data?.error?.message });
    }
  };

  const renameFolder = async ({ file_name }: { file_name: string }) => {
    try {
      const data = await renameFileAndFolder({
        groupId,
        payload: { file_name },
        fileId: selectedFileData[0]?.uuid,
      }).unwrap();
      successToast({ message: `Renamed to ${data.name}` });

      const updatedDocuments = entityFileAndFolder.map(
        (doc: { uuid: string }) => (doc.uuid === data.uuid ? data : doc)
      );
      setEntityFileAndFolder(updatedDocuments);
      setSelectedFiles([]);
      renameModal.close();
    } catch (error) {
      alertToast({ message: (error as BackendError)?.data?.error?.message });
    }
  };

  const handleMoveSuccess = () => {
    setEntityFileAndFolder((prev: fileAndFolder[]) =>
      prev.filter((file) => !selectedFileIds.includes(file.uuid))
    );
    setSelectedFiles([]);
  };

  const resetSelection = () => {
    setSelectedFiles([]);
    setEntityFileAndFolder(
      entityFileAndFolder.map((item: Record<string, unknown>) => ({
        ...item,
        isChecked: false,
      }))
    );
  };

  const handleGoToFolder = () => {
    handleFolderClick(selectedFileData[0]?.parent_uuid);
    setSelectedFiles([]);
  };

  return (
    <>
      <SelectedFloatingControl>
        {selectedItemLength > 0 && (
          <>
            <span className="t-whitespace-nowrap t-text-subtext t-text-text-60">
              {selectedItemLength} selected
            </span>
            <span className="t-text-neutral-10 t-h-full">
              <DividerLine />
            </span>
            <DocumentBulkAction
              onRename={renameModal.open}
              onCopy={() => copyToClipboard(linkText)}
              onMove={moveFileModal.open}
              onDownload={multipleDownloads}
              onDelete={deleteModal.open}
              goToFolder={handleGoToFolder}
              onMoveToRecycleBin={moveToRecycleBin.open}
            />
            <Button
              customType="ghost_icon"
              size="xsmall"
              onClick={resetSelection}
            >
              <Cross />
            </Button>
          </>
        )}
      </SelectedFloatingControl>

      {deleteModal.isOpen && (
        <DeleteConfirmationModal
          {...deleteModal}
          isLoading={isDeleting}
          confirm={multipleDelete}
          itemCount={selectedItemLength}
          type="MULTIPLE"
        />
      )}

      {moveToRecycleBin.isOpen && (
        <ConfirmMoveToRecycleBin
          {...moveToRecycleBin}
          isLoading={isMovingToBin}
          confirm={handleMoveToRecycleBin}
          selectedCount={selectedItemLength}
        />
      )}

      {renameModal.isOpen && (
        <RenameFileFolderModal
          renameModalShow={renameModal.isOpen}
          closeRenameModal={renameModal.close}
          itemType={selectedFileData[0]?.type}
          folderName={selectedFileData[0]?.name}
          renameFolder={renameFolder}
        />
      )}

      <MoveDocModal
        isOpen={moveFileModal.isOpen}
        close={moveFileModal.close}
        itemIds={selectedFileIds}
        onMoveSuccess={handleMoveSuccess}
        containsPrivateDocuments={containsPrivateDocuments}
      />
    </>
  );
};
